/**
 * Layer für Projekt-Windenergieanlagen.
 * Zeigt draggable Marker + Rotorradius-Kreis für jede Anlage mit Koordinaten.
 * Bei Überschneidung der Rotorradien werden die betroffenen Kreise rot eingefärbt.
 * Marker können nur innerhalb der Projektfläche verschoben werden (Ray-Casting-Constraint).
 *
 * Farbe richtet sich nach worst_parcel_status (schlechtester Flurstück-Status
 * im Rotorradius). Ohne Daten → Standard-Blau.
 */
import React, {
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { Marker, Circle } from "react-leaflet";
import L from "leaflet";
import { GenerationUnit } from "../../../types";
import { colors } from "../../../styles/colors";

const DEFAULT_COLOR = colors.map.plannedWindTurbines;
const OVERLAP_COLOR = colors.status.error.main;

const STATUS_COLOR_MAP: Record<string, string> = {
  Abgelehnt: colors.turbineStatus.abgelehnt,
  "Kein Kontakt": colors.turbineStatus.keinKontakt,
  Kontaktiert: colors.turbineStatus.kontaktiert,
  "Vertrag abgeschlossen": colors.turbineStatus.abgeschlossen,
};

function getTurbineColor(worstStatus?: string): string {
  if (!worstStatus) return DEFAULT_COLOR;
  return STATUS_COLOR_MAP[worstStatus] ?? DEFAULT_COLOR;
}

const iconCache = new Map<string, L.DivIcon>();

/**
 * Ausgewählt trägt die Anlage einen Ring in ihrer Farbe – wie der hervorgehobene Marker in der
 * Projektübersicht: Die Auswahl in der Tabelle darunter ist auch in der Karte zu sehen.
 */
function getTurbineIcon(
  color: string,
  draggable = true,
  selected = false,
): L.DivIcon {
  const key = `${color}-${draggable}-${selected}`;
  let icon = iconCache.get(key);
  if (!icon) {
    const ring = selected
      ? `border:2px solid ${color};border-radius:50%;background:${colors.background.paper};`
      : "";
    icon = L.divIcon({
      html: `
        <div style="width:24px;height:24px;display:flex;align-items:center;justify-content:center;${ring}${draggable ? "cursor:grab;" : ""}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24">
            <path d="M12 4v6m0 0l-4 4m4-4l4 4"
                  fill="none" stroke="${color}" stroke-width="2.5"
                  stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>`,
      className: "",
      iconSize: [24, 24],
      iconAnchor: [12, 12],
      popupAnchor: [0, -12],
    });
    iconCache.set(key, icon);
  }
  return icon;
}

/** Ray-Casting Point-in-Polygon — prüft ob [lat, lng] innerhalb des Polygons liegt */
function isPointInPolygon(
  lat: number,
  lng: number,
  polygon: [number, number][],
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [yi, xi] = polygon[i];
    const [yj, xj] = polygon[j];
    if (
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
}

interface TurbinePos {
  id: string;
  lat: number;
  lng: number;
  rotorradius: number;
}

/** Berechnet alle IDs, deren Rotorradius-Kreise sich überschneiden */
function computeOverlappingIds(positions: TurbinePos[]): Set<string> {
  const ids = new Set<string>();
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const a = positions[i];
      const b = positions[j];
      const dist = L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng));
      if (dist < a.rotorradius + b.rotorradius) {
        ids.add(a.id);
        ids.add(b.id);
      }
    }
  }
  return ids;
}

interface WindTurbinesLayerProps {
  anlagen: GenerationUnit[];
  visible: boolean;
  flaecheCoordinates?: [number, number][];
  onPositionChange?: (
    unitId: string,
    breitengrad: number,
    laengengrad: number,
  ) => void | Promise<void>;
  onSelect?: (unitId: string) => void;
  /** Hervorgehobene Anlage */
  selectedId?: string;
  /** Anlagen lassen sich nicht verschieben; ein Klick meldet die Anlage weiter über `onSelect` */
  readOnly?: boolean;
}

const WindTurbinesLayer: React.FC<WindTurbinesLayerProps> = ({
  anlagen,
  visible,
  flaecheCoordinates,
  onPositionChange,
  onSelect,
  selectedId,
  readOnly = false,
}) => {
  const [dragPositions, setDragPositions] = useState<
    Record<string, [number, number]>
  >({});

  const withCoords = useMemo(
    () => anlagen.filter((a) => a.breitengrad != null && a.laengengrad != null),
    [anlagen],
  );

  const overlappingIds = useMemo(() => {
    const positions: TurbinePos[] = withCoords
      .filter((a) => a.rotorradius != null && a.rotorradius > 0)
      .map((a) => {
        const drag = dragPositions[a.id];
        return {
          id: a.id,
          lat: drag ? drag[0] : a.breitengrad!,
          lng: drag ? drag[1] : a.laengengrad!,
          rotorradius: a.rotorradius!,
        };
      });
    return computeOverlappingIds(positions);
  }, [withCoords, dragPositions]);

  const handleDrag = useCallback((id: string, lat: number, lng: number) => {
    setDragPositions((prev) => ({ ...prev, [id]: [lat, lng] }));
  }, []);

  const handleDragEnd = useCallback(
    (id: string, lat: number, lng: number) => {
      setDragPositions((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return onPositionChange?.(id, lat, lng);
    },
    [onPositionChange],
  );

  if (!visible || withCoords.length === 0) return null;

  return (
    <>
      {withCoords.map((anlage) => (
        <DraggableTurbine
          key={anlage.id}
          anlage={anlage}
          flaecheCoordinates={flaecheCoordinates}
          isOverlapping={overlappingIds.has(anlage.id)}
          onDrag={handleDrag}
          onDragEnd={handleDragEnd}
          onSelect={onSelect}
          isSelected={anlage.id === selectedId}
          readOnly={readOnly}
        />
      ))}
    </>
  );
};

/** Einzelne Anlage mit Marker + Rotorradius-Kreis */
const DraggableTurbine: React.FC<{
  anlage: GenerationUnit;
  flaecheCoordinates?: [number, number][];
  isOverlapping: boolean;
  onDrag: (id: string, lat: number, lng: number) => void;
  onDragEnd: (id: string, lat: number, lng: number) => void | Promise<void>;
  onSelect?: (unitId: string) => void;
  isSelected?: boolean;
  readOnly?: boolean;
}> = ({
  anlage,
  flaecheCoordinates,
  isOverlapping,
  onDrag,
  onDragEnd,
  onSelect,
  isSelected = false,
  readOnly = false,
}) => {
  const [dragPos, setDragPos] = useState<[number, number] | null>(null);

  // Props fuehren wieder, sobald die neue Position gespeichert und durchgereicht ist
  useEffect(() => {
    setDragPos(null);
  }, [anlage.breitengrad, anlage.laengengrad]);

  const circleRef = useRef<L.Circle>(null);
  const glowRef = useRef<L.Circle>(null);

  const basePos: [number, number] = [anlage.breitengrad!, anlage.laengengrad!];
  const currentPos = dragPos ?? basePos;

  /** Letzte Position innerhalb der Projektflaeche, gesetzt beim Dragstart */
  const lastValidPos = useRef<[number, number]>(basePos);

  const hasBounds = flaecheCoordinates && flaecheCoordinates.length >= 3;
  const statusColor = getTurbineColor(anlage.worst_parcel_status);
  const circleColor = isOverlapping ? OVERLAP_COLOR : statusColor;

  const eventHandlers = useMemo(
    () => ({
      click: () => {
        onSelect?.(anlage.id);
      },
      dragstart: () => {
        lastValidPos.current = [anlage.breitengrad!, anlage.laengengrad!];
        onSelect?.(anlage.id);
      },
      drag: (e: L.LeafletEvent) => {
        const marker = e.target as L.Marker;
        const { lat, lng } = marker.getLatLng();

        if (hasBounds && !isPointInPolygon(lat, lng, flaecheCoordinates!)) {
          marker.setLatLng(lastValidPos.current);
          return;
        }

        const newPos: [number, number] = [lat, lng];
        lastValidPos.current = newPos;
        setDragPos(newPos);
        const latlng = marker.getLatLng();
        circleRef.current?.setLatLng(latlng);
        glowRef.current?.setLatLng(latlng);
        onDrag(anlage.id, lat, lng);
      },
      dragend: () => {
        const [lat, lng] = lastValidPos.current;
        // Position bleibt stehen, bis das Speichern durch ist — sonst springt die
        // Anlage sichtbar zurueck und wieder vor. Scheitert es, faellt sie auf die
        // Props zurueck, statt eine nie gespeicherte Position weiter anzuzeigen.
        void Promise.resolve(onDragEnd(anlage.id, lat, lng)).catch(() => {
          setDragPos(null);
        });
      },
    }),
    [
      anlage.id,
      anlage.breitengrad,
      anlage.laengengrad,
      onDrag,
      onDragEnd,
      onSelect,
      hasBounds,
      flaecheCoordinates,
    ],
  );

  const readOnlyEventHandlers = useMemo(
    () => ({ click: () => onSelect?.(anlage.id) }),
    [anlage.id, onSelect],
  );

  return (
    <>
      {anlage.rotorradius != null && anlage.rotorradius > 0 && (
        <>
          {/* Innerer Glow — simuliert Glaseffekt */}
          <Circle
            ref={glowRef}
            center={currentPos}
            radius={anlage.rotorradius * 0.7}
            interactive={false}
            pathOptions={{
              color: "transparent",
              weight: 0,
              fillColor: circleColor,
              fillOpacity: 0.06,
            }}
            pane="markerPane"
          />
          {/* Hauptkreis */}
          <Circle
            ref={circleRef}
            center={currentPos}
            radius={anlage.rotorradius}
            interactive={false}
            pathOptions={{
              color: circleColor,
              weight: 2.5,
              opacity: 0.6,
              fillColor: circleColor,
              fillOpacity: 0.1,
            }}
            pane="markerPane"
          />
        </>
      )}

      <Marker
        position={currentPos}
        icon={getTurbineIcon(
          isOverlapping ? OVERLAP_COLOR : statusColor,
          !readOnly,
          isSelected,
        )}
        draggable={!readOnly}
        eventHandlers={readOnly ? readOnlyEventHandlers : eventHandlers}
        pane="markerPane"
      />
    </>
  );
};

export default WindTurbinesLayer;
